import React from "react";

import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";

import { GreyPallet } from "../../../config/styles";

import MainText from "../../MainText";

type TextMainProps = {
  name: string;
  value: string | undefined;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  loading?: boolean;
  shrink?: boolean;
  lines?: number;
  borderColor?: "regular" | "light";
  textColor?: "regular" | "light";
  disabled?: boolean;
  small?: string | JSX.Element;
  inputType?: React.HTMLInputTypeAttribute | undefined;
  inputMode?:
    | "search"
    | "text"
    | "email"
    | "tel"
    | "url"
    | "none"
    | "numeric"
    | "decimal"
    | undefined;
  adornmentIcon?: React.ReactNode;
  adornmentClick?: () => void;
  adornmentDisabled?: boolean;
  autoCapitalize?: string;
};

export default function TextMain({
  name,
  value,
  onChange,
  disabled,
  small,
  borderColor,
  shrink = true,
  lines = 0,
  textColor,
  inputType,
  inputMode,
  adornmentIcon,
  adornmentClick,
  adornmentDisabled,
  autoCapitalize,
}: TextMainProps) {
  const [fieldValue, setFieldValue] = React.useState<string | undefined>("");

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const eventValue = event.currentTarget.value as string;
    setFieldValue(eventValue);
    onChange(eventValue);
  };

  const defaultBorderColor =
    borderColor === "regular" ? GreyPallet.RegularGhost : GreyPallet.LightGhost;
  const defaultTextColor =
    textColor === "regular" ? GreyPallet.RegularGhost : GreyPallet.LightGhost;

  return (
    <FormControl
      fullWidth
      sx={{
        "& .MuiFormLabel-root": {
          fontWeight: "bold",
          color: `${defaultTextColor} !important`,
        },
        mt: 1,
        mb: 2,
      }}
      variant="outlined"
    >
      <InputLabel shrink={shrink}>{name}</InputLabel>
      <OutlinedInput
        rows={lines}
        autoCapitalize={autoCapitalize}
        multiline={!!lines}
        fullWidth
        notched={shrink}
        type={inputType}
        inputMode={inputMode}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        label={name}
        sx={{
          borderRadius: "15px",

          height: !!lines ? undefined : 60,
          fontWeight: "bold",
          fontSize: 16,
          color: defaultTextColor,

          "& .MuiSvgIcon-root": {
            fontSize: 26,
          },

          "& .MuiInputLabel-root": {
            top: "2px",
          },

          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "3px !important",
            borderColor: `${defaultBorderColor} !important`,
            borderStyle: "solid",
            "& .Mui-focused": { borderColor: `${GreyPallet.RegularGhost} !important` },
          },
        }}
        endAdornment={
          adornmentIcon ? (
            <InputAdornment position="end" sx={{ mr: 1 }}>
              <IconButton onClick={adornmentClick} disabled={adornmentDisabled} edge="end">
                {adornmentIcon}
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />
      {small ? (
        <Box mt={1}>
          <MainText size="small" color="regular">
            {small}
          </MainText>
        </Box>
      ) : null}
    </FormControl>
  );
}
